// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useAppTracking } from '@/composables/app/useAppTracking';
import { useRouting } from '@/composables/routing/routing';
import { getCurrentPlatform, isHgPlatform } from '@/helpers/envService';
import { initializeFavouritesStore } from '@/store/modules/favourites';
import { HgSupportedLanguage, Icon } from '@smg-real-estate/domus';
import {
  getHgCoreLinkAttributes,
  getIs24CoreLinkAttributes,
  getMetaInfoCoreMetaProperties,
} from '@smg-real-estate/metatags-seo';
import { type Link, useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import { type RouteLocationResolved, useRoute } from 'vue-router';
import { useStore } from 'vuex';

type UseAppReturn = ReturnType<typeof useAppTracking> & {
  t: ReturnType<typeof useI18n>['t'];
  tm: ReturnType<typeof useI18n>['tm'];
  locale: string;
  PersonOutlineIcon: typeof Icon.PersonOutline;
  baseUrl: string;
  currentLocale: () => HgSupportedLanguage;
  getLanguageSwitchLink: (language: HgSupportedLanguage) => RouteLocationResolved | string;
};

export function useApp(): UseAppReturn {
  const { t, tm, locale } = useI18n();
  const route = useRoute();
  const store = useStore();
  const { getPageRoute } = useRouting();
  const tracking = useAppTracking(locale);

  const PersonOutlineIcon = Icon.PersonOutline;
  const baseUrl = import.meta.env.VUE_APP_BASE_URL;

  const currentLocale = () => {
    return locale.value as HgSupportedLanguage;
  };

  const lang = locale.value === HgSupportedLanguage.EN ? locale.value : `${locale.value}-CH`;

  const metaLinks: Link[] = isHgPlatform()
    ? getHgCoreLinkAttributes()
    : getIs24CoreLinkAttributes();
  const metaTags = getMetaInfoCoreMetaProperties();
  const platform = getCurrentPlatform();

  // Use `useHead` for meta tags and head configuration
  useHead({
    title: t(`favourites.page.title.${platform}`),
    htmlAttrs: { lang },
    link: metaLinks as Link[],
    meta: metaTags as Link[],
  });

  initializeFavouritesStore(store, route); // Assuming store can be null here, add store if needed

  const getLanguageSwitchLink = (language: HgSupportedLanguage): RouteLocationResolved | string => {
    if (!route.name) {
      return `/${language}`;
    }

    return {
      query: route.query,
      params: route.params,
      ...getPageRoute(route.name, language),
    };
  };

  return {
    t,
    tm,
    locale: locale.value,
    PersonOutlineIcon,
    baseUrl,
    currentLocale,
    ...tracking,
    getLanguageSwitchLink,
  };
}
